import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { UserContext } from '../../../context/authContext';
import { useAlert } from '../../../context/alertContext';
import axios from 'axios';
import { AsYouType } from 'libphonenumber-js';
import { Loader2 } from 'lucide-react';
import { IUserMDB } from '../../../lib/models/interfaces';

type newUser = {
  contactNumber: string;
  customerId: string;
  name: string;
  email: string;
  isAdmin: boolean;
  tncAcknowledge?: any[];
};

interface AddUserProps {
  currentUser?: IUserMDB;
  type?: string;
  setShowUpdateUser?: (arg: boolean) => void;
  setShowAddUser?: (arg: boolean) => void;
}
/**
 * Create New User
 * - user name, contact number and email used to create sub user.
 */
export const AddUser: React.FC<AddUserProps> = ({
  currentUser,
  type,
  setShowUpdateUser,
  setShowAddUser
}) => {
  const { auth, token, companyAccount, getUsers } = useContext(UserContext);
  const { setAlert } = useAlert();

  /// state
  const [user, setUser] = useState<newUser>({
    name: '',
    customerId: '',
    contactNumber: '',
    email: '',
    isAdmin: false,
    tncAcknowledge: []
  });
  const [emailError, setEmailError] = useState<string>('');

  const [createAccountLoading, setCreateAccountLoading] =
    useState<boolean>(false);

  //   use effects

  useEffect(() => {
    console.log('in start', currentUser);
    if (currentUser) {
      setUser({
        name: currentUser.name,
        customerId: currentUser.customerId,
        contactNumber: currentUser.contactNumber,
        email: currentUser.email,
        isAdmin: currentUser.isAdmin
      });
    }
  }, []);
  // functions
  const canEmailBeUsed = async (email: string) => {
    try {
      const checkIfEmailInUse = await axios.post(
        `${process.env.REACT_APP_KORU_API}/account?type=checkIfValidEmail`,
        { email: user.email },
        auth
      );
      const response = checkIfEmailInUse.data.result;
      return response;
    } catch (e) {
      setAlert('Error checking email', 'error');
    }
  };
  const createUser = async () => {
    if (!token || !setShowAddUser) return;
    try {
      console.log('token account', token);
      setCreateAccountLoading(true);
      const emailOkay = await canEmailBeUsed(user.email);

      if (emailOkay) {
        const results = await axios.post(
          `${process.env.REACT_APP_KORU_API}/user?type=addNew`,
          {
            email: user.email,
            name: user.name,
            isAdmin: user.isAdmin,
            contactNumber: user.contactNumber,
            customerId: companyAccount?.customerId
          },
          auth
        );
        setAlert(`Account Created`, 'success', 3);
        setShowAddUser(false);
        getUsers();
      } else {
        setEmailError('This email is already in use.');
      }

      setCreateAccountLoading(false);
    } catch (e) {
      const error = e as Error;
      setCreateAccountLoading(false);
      setAlert(`There was an error - ${error.message}`, 'error');
    }
  };

  const handleReset = () => {
    setEmailError('');
    setUser({
      name: '',
      email: '',
      contactNumber: '',
      customerId: '',
      isAdmin: false,
      tncAcknowledge: []
    });
  };

  const emailValid = () => {
    if (!user.email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(user.email);
  };
  const disableButton = useMemo(() => {
    const regex = /^\(\d{3}\) \d{3}-\d{4}$/;
    const isTelValid = regex.test(user.contactNumber);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const isEmailValid = emailRegex.test(user.email);
    if (user.email === '' && emailError) {
      setEmailError('');
    }
    if (
      !user.contactNumber ||
      !isTelValid ||
      !user.name ||
      !user.email ||
      !isEmailValid ||
      !companyAccount?.customerId
    ) {
      console.log(
        'returning true disable',
        isTelValid,
        user.contactNumber,
        user.name,
        isEmailValid,
        companyAccount?.customerId
      );
      return true;
    } else {
      return false;
    }
  }, [user]);
  const handleEmailBlur = async () => {
    if (user.email === '') return;
    if (!emailValid()) {
      setEmailError('Not a valid email address');
    }
    if (emailValid()) {
      const emailOkay = await canEmailBeUsed(user.email);
      if (!emailOkay) {
        setEmailError('This email address is already in use.');
      } else {
        setEmailError('');
      }
    }
  };

  const updateUser = async () => {
    if (!currentUser || !setShowUpdateUser) return;
    try {
      const attributesArray = [
        { Name: 'custom:isAdmin', Value: `${user.isAdmin ? 'true' : 'false'}` },
        { Name: 'name', Value: user.name }
      ];
      const results = await axios.post(
        `${process.env.REACT_APP_KORU_API}/user?type=update`,
        {
          username: currentUser.sub,
          contactNumber: user.contactNumber,
          isAdmin: user.isAdmin,
          UserAttributes: attributesArray,
          name: user.name
        },
        auth
      );
      getUsers();
      setShowUpdateUser(false);
      setAlert('User Updated', 'success', 3);
    } catch (e) {
      const error = e as Error;
      console.log('error from update', e);
      setAlert(`${error.message}`, 'error');
    }
  };

  return (
    <div className="min-h-[100%] w-full flex items-center justify-center p-6 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative z-10 w-full max-w-3xl"
      >
        <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]">
          {/* Header */}
          <div className="px-8 pt-8 pb-4 text-center">
            <img
              src="/korulogo.png"
              alt="Koru Logo"
              className="mx-auto mb-4 h-12 w-12 object-contain"
              loading="eager"
              decoding="async"
            />
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
              {type !== 'update' ? 'Create User' : 'Update User'}
            </h1>
          </div>
          {/* Form */}
          <form
            className=" px-8 pb-8 pt-4 space-y-5"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="name"
                  className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder=""
                  onChange={(e) =>
                    setUser((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value
                    }))
                  }
                  value={user.name}
                  className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Email
                </label>
                <input
                  disabled={type === 'update' ? true : false}
                  id="email"
                  name="email"
                  onBlur={handleEmailBlur}
                  type="text"
                  placeholder=""
                  onChange={(e) =>
                    setUser((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value
                    }))
                  }
                  value={user.email}
                  className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                />

                {type === 'update' && (
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Email Address cannot be changed
                  </p>
                )}

                {emailError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {emailError}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="telephone"
                  className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Contact Number
                </label>
                <input
                  id="telephone"
                  name="contactNumber"
                  type="text"
                  placeholder="(555) 867 5309"
                  value={user.contactNumber}
                  onChange={(e) => {
                    if (e.target.value.length <= 5) {
                      setUser((prev) => ({
                        ...prev,
                        [e.target.name]: e.target.value
                      }));
                    } else {
                      const newNumber = new AsYouType('US').input(
                        e.target.value
                      );
                      setUser((prev) => ({
                        ...prev,
                        [e.target.name]: newNumber
                      }));
                    }
                  }}
                  className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                />
              </div>
              <div>
                <label
                  htmlFor="role"
                  className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Role
                </label>

                <select
                  id="role"
                  name="isAdmin"
                  className="block w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 px-3 py-2.5 text-slate-900 dark:text-slate-100 outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 transition"
                  onChange={(e) => {
                    console.log('role value', e.target.value);
                    const newRoleValue =
                      e.target.value === 'true' ? true : false;
                    setUser((prev) => ({
                      ...prev,
                      [e.target.name]: newRoleValue
                    }));
                  }}
                  value={user.isAdmin ? 'true' : 'false'}
                >
                  <option value={'false'}>User</option>
                  <option value="true">Admin</option>
                </select>
              </div>
            </div>

            {/* No submit button or logic */}
            {type !== 'update' && (
              <div className="group relative inline-flex w-full items-center justify-around">
                <button
                  className="inline-flex w-[40%] items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-white transition hover:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                  onClick={() => {
                    if (setShowAddUser) {
                      setShowAddUser(false);
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  className="inline-flex w-[40%] items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-white transition hover:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                  onClick={() => createUser()}
                  disabled={disableButton}
                >
                  {createAccountLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating User
                    </>
                  ) : (
                    <>Create User</>
                  )}
                </button>
              </div>
            )}
            {type === 'update' && currentUser && (
              <div className="group relative inline-flex w-full items-center justify-around">
                <button
                  className="inline-flex w-[40%] items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-white transition hover:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                  onClick={() => {
                    if (setShowUpdateUser) {
                      setShowUpdateUser(false);
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  className="inline-flex w-[40%] items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-white transition hover:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                  onClick={() => updateUser()}
                  disabled={disableButton}
                >
                  {createAccountLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Updating User
                    </>
                  ) : (
                    <>Update User</>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </motion.div>
    </div>
  );
};
